import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import type { PortalUser } from '../AffiliatePortalApp';
import ThemeToggle from '../ThemeToggle';

const API = import.meta.env.VITE_API_URL;

interface Props {
  onLogin: (u: PortalUser) => void;
  navigate: (path: string) => void;
}

export default function Login({ onLogin, navigate }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await fetch(`${API}/affiliate-portal/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const json = (await res.json()) as { success: boolean; user?: PortalUser; error?: string };
    setLoading(false);
    if (!json.success || !json.user) {
      setError(json.error || 'Login failed');
      return;
    }
    onLogin(json.user);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 font-inter flex flex-col">
      <div className="border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-xl font-bold">
            <span className="text-cyan-600 dark:text-cyan-400">OAK</span>
            <span className="text-slate-700 dark:text-slate-300 text-base">Affiliates</span>
          </button>
          <ThemeToggle className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800" />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Sign in</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Access your affiliate portal</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
                <AlertCircle className="w-5 h-5 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-3 py-2" />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-2 rounded-lg font-medium disabled:opacity-50">
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-4 text-center text-sm space-y-1">
              <button onClick={() => navigate('/forgot-password')} className="text-cyan-600 dark:text-cyan-400 hover:underline">Forgot password?</button>
              <div className="text-slate-600 dark:text-slate-400">
                Not an affiliate yet? <button onClick={() => navigate('/apply')} className="text-cyan-600 dark:text-cyan-400 hover:underline">Apply</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
